/**
 * SignUpSignInForm presents a form to authenticate by logging in or creating an account and calls
 * the appropriate authentication function
 */

import React, { useState, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import { gsColors } from '../../styles/Variables'
import {
  isEmailBoolean,
  isPhoneValid,
  signInDisabled
} from '../../helpers/final-form-input-components'
import { useBooleanState } from '../../helpers'
import SignUpFormError from './Error'
import { signIn, signUp } from '../../services/Authenticator'
import Input from '../Input'

import Button from '../Button'
import CheckboxSingle from '../../components/book/steps/components/CheckboxSingle'

import { Header1, Text, Button as TextButton } from '../../styles/Components'
import { SignUpSection } from './styles'
import SignInModal from './SignInModal'
import { useRouter } from 'next/router'
import { useUserContext } from '../UserContext'

const signUpSignInState = {
  nameFirst: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  nameLast: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  phone: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  email: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  password: {
    touched: false,
    value: '',
    error: false,
    active: false,
  }
}

const signUpSignInReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_TEXT':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          value: action.value,
          error: false
        }
      }
    case 'FOCUS':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          touched: true,
          active: true,
        }
      }
    case 'BLUR':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          active: false
        }
      }
    case 'ERROR':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          error: true,
          active: false
        }
      }
    default:
      return state
  }
}


/*
 * initSignUpForm: toggles default formMode to singupForm
 * showHeadlines: flag to toggle main headers from signin/signup page
 *     - if true it shows Signin and Signup titles
 *     - false in booking flow to disable the headers and show different headers
 */
const SignUpSignInForm = ({
  onSuccess,
  showHeadlines = false,
  initSignUpForm = true,
  referredByCode = null,
}) => {
  const [state, dispatch] = useReducer(signUpSignInReducer, signUpSignInState)

  const router = useRouter()
  const [formModeSignUp, toggleFormModeSignUp] = useBooleanState(initSignUpForm)
  const [termsAccepted, toggleTermsAccepted] = useBooleanState()
  const [rememberAuth, toggleRememberAuth] = useBooleanState(false)
  const [submitError, setSubmitError] = useState(null)
  const [passwordResetModal, togglePasswordResetModal] = useBooleanState(false)
  const [working, setWorking] = useState(false)
  const { setUser } = useUserContext()
  const resetParam = router?.query?.resetPassword

  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 768)

    const handleResize = () => {
      if (
        (isDesktop && window.innerWidth <= 768) ||
        (!isDesktop && window.innerWidth >= 768)
      ) {
        setIsDesktop(window.innerWidth >= 768)
      }
    }

    window.addEventListener('resize', handleResize, { passive: true })
    return () => window.removeEventListener('resize', handleResize)
  }, [isDesktop])

  const doSubmit = async ({ email, password, nameFirst, nameLast, phone }) => {
    let user
    if (formModeSignUp) {
      // sign up
      user = await signUp(
        email,
        nameFirst,
        nameLast,
        phone,
        password,
        referredByCode
      )
    } else {
      // sign in
      user = await signIn(email, password, rememberAuth)
    }

    // await new Promise((res) => res(onSuccess()))
    setUser({ ...user })
    onSuccess()
  }

  // calls provided onSubmit function catching any errors and returning as form errors for display
  const onSubmitWithErrorHandling = async (e) => {
    e.preventDefault()
    setWorking(true)
    try {
      await doSubmit({
        email: state.email.value,
        nameFirst: state.nameFirst.value,
        nameLast: state.nameLast.value,
        phone: state.phone.value.replace(/[^+\d]+/g, ''),
        password: state.password.value
      })
      setWorking(false)
    } catch (error) {
      setWorking(false)
      setSubmitError(error)
    }
  }

  const CheckboxText = () => {
    return (
      <span>
        I agree to Glamsquad&rsquo;s
        <a href="/terms" target="_blank">Terms of Service</a> and <a href="/privacypolicy" target="_blank">Privacy Policy</a>
      </span>
    )
  }

  const RememberMeText = () => {
    return <div className="remember-me-text">Remember me</div>
  }

  // when coming from bpPortal to reset password this auto toggles the reset modal
  useEffect(() => {
    if (!passwordResetModal && !!resetParam) {
      togglePasswordResetModal()
    }
  }, [resetParam, passwordResetModal, togglePasswordResetModal])

  const handleOnFocus = ({ target: { name } }) =>
    dispatch({ type: 'FOCUS', name })

  const handleInput = ({ target: { name, value } }) =>
    dispatch({ type: 'CHANGE_TEXT', name, value })


  const handleOnBlur = ({ target: { name, value } }) => {
    if (
      (name === 'email' && !isEmailBoolean(value)) ||
      (name === 'phone' && !isPhoneValid(value)) ||
      (name === 'password' && value.length < 6)
    ) {
      dispatch({
        type: 'ERROR',
        name,
      })
    } else {
      dispatch({
        type: 'BLUR',
        name
      })
    }
  }

  const submitDisabled = signInDisabled(state, termsAccepted, working, formModeSignUp)

  return (
    <>
      {/* Toggles for Form Headline Modes */}
      {!formModeSignUp && (
        <Header1 margin="80px 0 12px" style={{ order: 1 }} align="center">
          Sign In
        </Header1>
      )}
      {showHeadlines && formModeSignUp && (
        <Header1 margin="80px 0 12px" style={{ order: 1 }} align="center">
          Create an account
        </Header1>
      )}
      {!showHeadlines && formModeSignUp && (
        <Header1
          margin="80px 0 12px"
          style={{ order: 1 }}
          align="center"
        >
          Finish setting up your account.
        </Header1>
      )}

      <div
        style={{
          order: 2,
          flexDirection: 'row',
          display: 'flex',
          gap: 0,
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
        }}
      >
        {formModeSignUp ? (<>
          <Text
            variant="small"
            color={gsColors.black}
            style={{ textAlign: 'center' }}
            weight={400}
          >
            Already have a Glamsquad account?&nbsp;
          </Text>
          <TextButton
            style={{ minWidth: '0', fontWeight: 600, padding: 0, margin: 0 }}
            color={gsColors.sunsetOrange}
            outline
            noBorder
            onClick={toggleFormModeSignUp}
            id="main-content"
          >
          Sign in.
          </TextButton>
        </>) : (<>
          <Text
            variant="small"
            color={gsColors.black}
            style={{ textAlign: 'center' }}
            weight={400}
          >
          New to Glamsquad?&nbsp;
          </Text>
          <TextButton
            style={{ minWidth: '0', fontWeight: 600, padding: 0, margin: 0 }}
            color={gsColors.sunsetOrange}
            outline
            noBorder
            onClick={toggleFormModeSignUp}
            id="main-content"
          >
            {showHeadlines ? 'Create an account.' : 'Finish setting up your account.'}
          </TextButton>
        </>)}
      </div>

      <SignUpSection>
        {formModeSignUp ? (
          <form
            id='sign-up-form'
            name='sign-up-form'
            onSubmit={onSubmitWithErrorHandling}            
            autoComplete='on'
          >
            <div className="field-container">
              <Input
                className='name-first'
                placeholder='First Name'
                type='text'
                name='nameFirst'
                value={state.nameFirst.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete='given-name'
              />
            </div>
            <div className="field-container">
              <Input
                className='name-last'
                placeholder='Last Name'
                type='text'
                name='nameLast'
                value={state.nameLast.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete='family-name'
              />
            </div>
            <div
              className={`field-container phone-input-container ${state.phone.error ? 'phone-error' : ''}`}
            >
              <Input
                name='phone'
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}                
                value={state.phone.value}
                onChange={handleInput}
                className={state.phone.error ? 'input-error' : ''}
                autoComplete='tel'
              />
              <label className={(state.phone.active || state.phone.value.length) ? 'active' : ''}>Phone Number</label>
              {state.phone.error && <p className='error-message'>Please enter a valid phone number</p>}
            </div>
            <div className="field-container email-container">
              <Input
                className={`email-address ${state.email.error ? 'input-error' : ''}`}
                placeholder='Email Address'
                type='email'
                name='email'
                value={state.email.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete='email'
              />
              {state.email.error && <p className='error-message'>Please enter a valid email</p>}
            </div>
            <div className="field-container password-container">
              <Input
                className={`password ${state.password.error ? 'input-error' : ''}`}
                placeholder='Create a Password'
                type='password'
                name='password'
                value={state.password.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete='new-password'
              />
              <p className={`field-hint ${state.password.error ? 'error-message' : ''}`}>
                Password must be at least 6 characters long.
              </p>
            </div>
            <div className="field-container terms-container">
              <CheckboxSingle
                name="terms"
                onChange={toggleTermsAccepted}
                textComponent={CheckboxText}
                value={termsAccepted}
              />
            </div>
            <SignUpFormError error={submitError} />
            <div className="field-container">
              <Button margin="10px 0 0" large fill='true'
                id="submit-btn"
                type="submit"
                disabled={submitDisabled}
                working={working}
              >
                Continue
              </Button>
            </div>


          </form>
        ) : (
          <form
            id='sign-in-form'
            name='sign-in-form'
            onSubmit={onSubmitWithErrorHandling}
            data-testid="auth-forms"
          >
            {/* Sign In Form */}
            <div className="field-container email-container">
              <Input
                className={`email-address ${state.email.error ? 'input-error' : ''}`}
                placeholder='Email Address'
                type='email'
                name='email'
                value={state.email.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
              />
              {state.email.error && <p className='error-message'>Please enter a valid email</p>}
            </div>
            <div className="field-container password-container">
              <Input
                className={`password ${state.password.error ? 'input-error' : ''}`}
                placeholder='Password'
                type='password'
                name='password'
                value={state.password.value}
                onChange={handleInput}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
              />
            </div>
            <TextButton
              style={{ minWidth: '0', fontWeight: 600, padding: 0, margin: 0 }}
              preset='small'
              outline
              noBorder
              color={gsColors.sunsetOrange}
              onClick={togglePasswordResetModal}
              type='button'
            >
              Forgot Password?
            </TextButton>

            <div className="field-container remember-me-toggle">
              <CheckboxSingle
                name="rememberMe"
                onChange={toggleRememberAuth}
                textComponent={RememberMeText}
                value={rememberAuth}
              />
            </div>
            <div className="field-container">
              <SignUpFormError error={submitError} />
            </div>
            <div className="field-container">
              <Button margin="10px 0 0" large fill='true'
                type="submit"
                disabled={submitDisabled}
                working={working}
              >
                Sign In
              </Button>
            </div>
            <SignInModal
              isOpen={passwordResetModal}
              close={() => {
                // removes the search from location to prevent modal from being uncloseable 
                if (resetParam) {
                  router.replace('/signin')
                }
                togglePasswordResetModal()
              }}
              isReset={true}
              isDesktop={isDesktop}
            />
          </form>
        )}
      </SignUpSection>
    </>
  )
}

SignUpSignInForm.propTypes = {
  onSuccess: PropTypes.func.isRequired
}

export default SignUpSignInForm
